import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, MapPin, Star, Phone, Mail, Bell, Send, CheckCircle, Shield, ArrowRight, X, Map, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import FoundingMemberBadge from '../components/FoundingMemberBadge';
import TopRatedBadge from '../components/TopRatedBadge';
import EngineerMap from '../components/EngineerMap';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { trackPageView, trackEngineerClick } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REFERRAL_SOURCES = [
  { value: '', label: 'Please select...' },
  { value: 'tv', label: 'TV Advert' },
  { value: 'radio', label: 'Radio' },
  { value: 'google', label: 'Google / Search Engine' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'gas_safe_magazine', label: 'Gas Safe Magazine' },
  { value: 'trade_magazine', label: 'Other Trade Magazine' },
  { value: 'newspaper', label: 'Newspaper' },
  { value: 'billboard', label: 'Billboard / Poster' },
  { value: 'van_advert', label: 'Van / Vehicle Advert' },
  { value: 'word_of_mouth', label: 'Word of Mouth' },
  { value: 'friend_family', label: 'Friend / Family' },
  { value: 'other', label: 'Other' }
];

const FindEngineer = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [postcode, setPostcode] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [engineerReviews, setEngineerReviews] = useState([]);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({ name: '', email: '', notify: true, referralSource: '' });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ customerName: '', customerPhone: '', rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [badgeTooltip, setBadgeTooltip] = useState(null);
  const [searchError, setSearchError] = useState('');

  const validatePostcode = (input) => {
    const trimmed = input.trim().toUpperCase();
    if (!trimmed) return { valid: false, error: '' };
    
    // Check if it looks like a town/city name (all letters, no numbers, 4+ chars)
    if (/^[A-Z\s]{4,}$/.test(trimmed) && !/^[A-Z]{1,2}\s*$/.test(trimmed)) {
      return { valid: false, error: `"${trimmed}" looks like a town name. Please enter a postcode instead, e.g. SW1A 1AA or CM11` };
    }
    
    // Too short (single letter)
    if (trimmed.length < 2) {
      return { valid: false, error: 'Please enter at least the first part of your postcode, e.g. CM11 or SW1A' };
    }
    
    // Must start with a letter
    if (!/^[A-Z]/.test(trimmed)) {
      return { valid: false, error: 'UK postcodes start with a letter. Please try again, e.g. SW1A 1AA' };
    }
    
    // Must contain at least one number
    if (!/\d/.test(trimmed)) {
      return { valid: false, error: 'That doesn\'t look like a valid postcode. Try entering yours, e.g. CM11 2FA' };
    }
    
    // Check for obviously invalid patterns (too many letters in a row, too long, etc.)
    if (trimmed.replace(/\s/g, '').length > 8) {
      return { valid: false, error: 'That postcode seems too long. UK postcodes are 5-7 characters, e.g. SW1A 1AA' };
    }
    
    return { valid: true, error: '' };
  };

  useEffect(() => {
    trackPageView('find');
    const urlPostcode = searchParams.get('postcode');
    if (urlPostcode && !hasSearched) {
      setPostcode(urlPostcode);
      performSearch(urlPostcode);
    }
  }, [searchParams]);

  const performSearch = async (searchPostcode) => {
    if (searchPostcode.trim()) {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/engineers/search`, {
          params: { postcode: searchPostcode.trim() }
        });
        if (response.data.engineers) {
          setSearchResults(response.data.engineers);
          setHasSearched(true);
        }
      } catch (error) {
        toast({ title: "Search Error", description: "Unable to search. Please try again.", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchError('');
    const result = validatePostcode(postcode);
    if (!result.valid) {
      setSearchError(result.error);
      return;
    }
    performSearch(postcode);
  };

  const getEngineerReviews = async (engineerId) => {
    try {
      const response = await axios.get(`${API}/engineers/${engineerId}/reviews`);
      return response.data.success ? response.data.reviews : [];
    } catch (error) {
      return [];
    }
  };

  const handleViewDetails = async (engineer) => {
    trackEngineerClick(engineer.id, 'find');
    setSelectedEngineer(engineer);
    setShowReviewForm(false);
    // Lazy-fetch full engineer detail (workImages etc. are stripped from search results
    // to keep the response small — this call brings them in).
    try {
      const fullRes = await axios.get(`${API}/engineers/${engineer.id}`, { timeout: 20000 });
      if (fullRes.data?.success && fullRes.data.engineer) {
        setSelectedEngineer(prev => (prev && prev.id === engineer.id) ? { ...prev, ...fullRes.data.engineer } : prev);
      }
    } catch (err) {
      console.warn('Could not load full engineer details:', err?.message || err);
    }
    const reviews = await getEngineerReviews(engineer.id);
    setEngineerReviews(reviews);
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setSubmittingRequest(true);
    try {
      const response = await axios.post(`${API}/engineer-requests`, {
        postcode, name: requestForm.name || null, email: requestForm.email || null,
        notify: requestForm.notify, referralSource: requestForm.referralSource || null
      });
      if (response.data.success) {
        toast({
          title: "Request Submitted!",
          description: requestForm.email ? "Check your inbox for confirmation!" : "We'll prioritise finding engineers in your area."
        });
        setShowRequestForm(false);
        setRequestForm({ name: '', email: '', notify: true, referralSource: '' });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to submit. Please try again.", variant: "destructive" });
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!selectedEngineer) return;
    if (!reviewForm.customerPhone || reviewForm.customerPhone.length < 10) {
      toast({ title: "Phone Required", description: "Please enter your phone number for verification.", variant: "destructive" });
      return;
    }
    setSubmittingReview(true);
    try {
      const response = await axios.post(`${API}/reviews`, {
        engineerId: selectedEngineer.id, 
        customerName: reviewForm.customerName,
        customerPhone: reviewForm.customerPhone,
        rating: reviewForm.rating, 
        comment: reviewForm.comment
      });
      if (response.data.success) {
        toast({ title: "Review Submitted!", description: response.data.message || "Your review will be published after verification." });
        setShowReviewForm(false);
        setReviewForm({ customerName: '', customerPhone: '', rating: 5, comment: '' });
      }
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to submit.", variant: "destructive" });
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <div className="find-engineer-new" onClick={() => badgeTooltip && setBadgeTooltip(null)}>
      {/* Slim Gas Emergency Banner - Top */}
      <div className="gas-emergency-slim">
        <AlertTriangle size={16} />
        <span><strong>Smell gas?</strong> Call <a href="tel:0800111999">0800 111 999</a> immediately</span>
      </div>

      {/* Hero Search */}
      <section className="find-hero-new">
        <div className="find-hero-content">
          <h1 className="find-headline">FIND YOUR ENGINEER</h1>
          <p className="find-subline">Enter your postcode to find vetted Gas Safe professionals</p>
          
          <form onSubmit={handleSearch} className="find-search-form" data-testid="find-search-form">
            <div className="find-search-wrapper">
              <MapPin className="find-search-icon" size={22} />
              <input
                type="text"
                placeholder="Enter postcode (e.g., SW1A 1AA)"
                value={postcode}
                onChange={(e) => { setPostcode(e.target.value); setSearchError(''); }}
                className="find-search-input"
                data-testid="find-postcode-input"
              />
              <Button type="submit" className="find-search-btn" disabled={loading} data-testid="find-search-btn">
                {loading ? 'Searching...' : <><Search size={18} /> Search</>}
              </Button>
            </div>
          </form>

          {searchError && (
            <div className="find-search-error" data-testid="search-error-message">
              <AlertTriangle size={16} />
              <span>{searchError}</span>
            </div>
          )}

          <div className="find-trust-row">
            <span><Shield size={16} /> Gas Safe Verified</span>
            <span><CheckCircle size={16} /> Insurance Checked</span>
            <span><Star size={16} /> Customer Reviewed</span>
          </div>

          <Button 
            variant="outline" 
            className="find-map-btn"
            onClick={() => setShowMap(true)}
            data-testid="view-map-btn"
          >
            <Map size={18} /> View Coverage Map
          </Button>
        </div>
      </section>

      {/* Results Section */}
      <section className="find-results-section">
        <div className="container-premium">
          {/* Loading State */}
          {loading && (
            <div className="find-loading">
              <div className="loading-spinner"></div>
              <p>Searching for engineers in your area...</p>
            </div>
          )}

          {/* No Results */}
          {hasSearched && !loading && searchResults.length === 0 && (
            <div className="no-results-new" data-testid="no-results">
              <div className="no-results-content">
                <div className="no-results-icon">
                  <MapPin size={48} />
                </div>
                <h2>No Engineers in {postcode.toUpperCase()} Yet</h2>
                <p>We're expanding our network of vetted engineers. Let us know you're looking!</p>
                
                {!showRequestForm ? (
                  <Button onClick={() => setShowRequestForm(true)} className="btn-request" data-testid="request-engineer-btn">
                    <Bell size={18} /> Request an Engineer
                  </Button>
                ) : (
                  <form onSubmit={handleSubmitRequest} className="request-form-new">
                    <h3>Get notified when engineers are available</h3>
                    <div className="request-fields">
                      <Input
                        type="text" placeholder="Your name (optional)"
                        value={requestForm.name}
                        onChange={(e) => setRequestForm({ ...requestForm, name: e.target.value })}
                      />
                      <Input
                        type="email" placeholder="Your email (optional)"
                        value={requestForm.email}
                        onChange={(e) => setRequestForm({ ...requestForm, email: e.target.value })}
                      />
                    </div>
                    <div className="request-field">
                      <label>How did you hear about us?</label>
                      <select
                        value={requestForm.referralSource}
                        onChange={(e) => setRequestForm({ ...requestForm, referralSource: e.target.value })}
                        className="request-select"
                      >
                        {REFERRAL_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <label className="request-checkbox">
                      <input
                        type="checkbox" checked={requestForm.notify}
                        onChange={(e) => setRequestForm({ ...requestForm, notify: e.target.checked })}
                      />
                      <span>Notify me when an engineer is available</span>
                    </label>
                    <div className="request-actions">
                      <Button type="submit" disabled={submittingRequest}>
                        <Send size={16} /> {submittingRequest ? 'Submitting...' : 'Submit'}
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => setShowRequestForm(false)}>Cancel</Button>
                    </div>
                  </form>
                )}

                <div className="no-results-cta">
                  <p>Are you a Gas Safe engineer?</p>
                  <Link to="/for-engineers">Be the first in your area <ArrowRight size={16} /></Link>
                </div>
              </div>
            </div>
          )}

          {/* Results Grid */}
          {searchResults.length > 0 && (
            <div className="results-container">
              <div className="results-header-new">
                <h2>Engineers Near {postcode.toUpperCase()}</h2>
                <span className="results-count">{searchResults.length} vetted professional{searchResults.length !== 1 ? 's' : ''}</span>
              </div>

              <div className="engineers-grid-new">
                {searchResults.map((engineer) => (
                  <div key={engineer.id} className="engineer-card-new" data-testid={`engineer-card-${engineer.id}`}>
                    <div className="engineer-card-top">
                      <div className="engineer-initials">
                        {engineer.fullName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div className="engineer-info-main">
                        <h3>{engineer.fullName}</h3>
                        <p className="business-name">{engineer.businessName}</p>
                      </div>
                      {engineer.isFoundingMember && (
                        <div className="badge-clickable" onClick={(e) => { e.stopPropagation(); setBadgeTooltip(badgeTooltip === `founder-${engineer.id}` ? null : `founder-${engineer.id}`); }} data-testid="founder-badge-click">
                          <FoundingMemberBadge size="small" />
                          {badgeTooltip === `founder-${engineer.id}` && (
                            <div className="badge-tooltip" data-testid="founder-tooltip">
                              <strong>Founding Member</strong>
                              <p>One of the first engineers in their area to join The Gas Guide. Early adopters who believed in raising industry standards from day one.</p>
                            </div>
                          )}
                        </div>
                      )}
                      {engineer.rating >= 4.7 && engineer.reviewCount >= 3 && (
                        <div className="badge-clickable" onClick={(e) => { e.stopPropagation(); setBadgeTooltip(badgeTooltip === `toprated-${engineer.id}` ? null : `toprated-${engineer.id}`); }} data-testid="toprated-badge-click">
                          <TopRatedBadge size="small" />
                          {badgeTooltip === `toprated-${engineer.id}` && (
                            <div className="badge-tooltip" data-testid="toprated-tooltip">
                              <strong>Top Rated Engineer</strong>
                              <p>Rated 4.7 stars or higher by verified customers. This badge is earned through consistently excellent service and reviews.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="engineer-rating-row">
                      <div className="rating-stars-new">
                        <Star fill="currentColor" size={16} />
                        <span>{engineer.rating}</span>
                      </div>
                      <span className="rating-count">({engineer.reviewCount} reviews)</span>
                      <Badge variant="outline" className="verified-badge">
                        <Shield size={12} /> Verified
                      </Badge>
                    </div>

                    <div className="engineer-meta">
                      <span><strong>Gas Safe:</strong> {engineer.gasSafeNumber}</span>
                      <span><strong>Experience:</strong> {engineer.yearsTrading}+ years</span>
                    </div>

                    {engineer.services?.length > 0 && (
                      <div className="engineer-services-row">
                        {engineer.services.slice(0, 3).map((service, i) => (
                          <Badge key={i} variant="secondary" className="service-tag">
                            {service === 'domestic' ? 'Domestic' : service === 'commercial' ? 'Commercial' :
                             service === 'lpg' ? 'LPG' : service === 'landlord' ? 'Landlord' : service}
                          </Badge>
                        ))}
                        {engineer.services.length > 3 && <Badge variant="secondary">+{engineer.services.length - 3}</Badge>}
                      </div>
                    )}

                    {engineer.bio && (
                      <p className="engineer-bio-preview">{engineer.bio.length > 120 ? engineer.bio.slice(0, 120) + '...' : engineer.bio}</p>
                    )}

                    {engineer.accreditations?.length > 0 && (
                      <div className="engineer-accreditations-row">
                        {engineer.accreditations.slice(0, 3).map((acc, i) => (
                          <span key={i} className="accreditation-tag-sm">{acc}</span>
                        ))}
                        {engineer.accreditations.length > 3 && <span className="accreditation-tag-sm">+{engineer.accreditations.length - 3}</span>}
                      </div>
                    )}

                    <div className="engineer-actions-new">
                      <Button onClick={() => handleViewDetails(engineer)} className="btn-details">
                        View Profile
                      </Button>
                      <a href={`tel:${engineer.phone.replace(/\s/g, '')}`} className="btn-call">
                        <Phone size={16} /> Call
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Engineer Detail Modal */}
      {selectedEngineer && (
        <div className="modal-overlay-new" onClick={() => setSelectedEngineer(null)}>
          <div className="modal-content-new" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedEngineer(null)}>
              <X size={24} />
            </button>
            
            <div className="modal-header">
              <div className="modal-engineer-info">
                <div className="modal-initials">
                  {selectedEngineer.fullName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <h2>{selectedEngineer.fullName}</h2>
                  <p>{selectedEngineer.businessName}</p>
                </div>
              </div>
              {selectedEngineer.isFoundingMember && (
                <div className="badge-clickable" onClick={() => setBadgeTooltip(badgeTooltip === 'modal-founder' ? null : 'modal-founder')} data-testid="modal-founder-badge-click">
                  <FoundingMemberBadge size="medium" />
                  {badgeTooltip === 'modal-founder' && (
                    <div className="badge-tooltip" data-testid="modal-founder-tooltip">
                      <strong>Founding Member</strong>
                      <p>One of the first engineers in their area to join The Gas Guide. Early adopters who believed in raising industry standards from day one.</p>
                    </div>
                  )}
                </div>
              )}
              {selectedEngineer.rating >= 4.7 && selectedEngineer.reviewCount >= 3 && (
                <div className="badge-clickable" onClick={() => setBadgeTooltip(badgeTooltip === 'modal-toprated' ? null : 'modal-toprated')} data-testid="modal-toprated-badge-click">
                  <TopRatedBadge size="medium" />
                  {badgeTooltip === 'modal-toprated' && (
                    <div className="badge-tooltip" data-testid="modal-toprated-tooltip">
                      <strong>Top Rated Engineer</strong>
                      <p>Rated 4.7 stars or higher by verified customers. This badge is earned through consistently excellent service and reviews.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-rating">
              <Star fill="currentColor" size={24} />
              <span className="rating-big">{selectedEngineer.rating}</span>
              <span className="rating-sub">({selectedEngineer.reviewCount} reviews)</span>
            </div>

            <div className="modal-details-grid">
              <div className="detail-box">
                <label>Gas Safe Number</label>
                <span>{selectedEngineer.gasSafeNumber}</span>
              </div>
              <div className="detail-box">
                <label>Experience</label>
                <span>{selectedEngineer.yearsTrading}+ years</span>
              </div>
              <div className="detail-box full">
                <label>Coverage Areas</label>
                <span>{selectedEngineer.postcodes.join(', ')}</span>
              </div>
            </div>

            {selectedEngineer.specialties?.length > 0 && (
              <div className="modal-specialties">
                <label>Specialties</label>
                <div className="specialty-tags">
                  {selectedEngineer.specialties.map((s, i) => <Badge key={i} variant="secondary">{s}</Badge>)}
                </div>
              </div>
            )}

            {selectedEngineer.bio && (
              <div className="modal-bio">
                <p>{selectedEngineer.bio}</p>
              </div>
            )}

            {selectedEngineer.accreditations?.length > 0 && (
              <div className="modal-specialties">
                <label>Accreditations</label>
                <div className="specialty-tags">
                  {selectedEngineer.accreditations.map((a, i) => <span key={i} className="accreditation-tag-sm">{a}</span>)}
                </div>
              </div>
            )}

            {selectedEngineer.workImages?.length > 0 && (
              <div className="modal-work-photos">
                <label>Work Photos</label>
                <div className="modal-photos-grid">
                  {selectedEngineer.workImages.slice(0, 4).map((img, i) => (
                    <img key={i} src={img} alt={`Work ${i + 1}`} className="modal-work-photo" />
                  ))}
                </div>
              </div>
            )}

            <div className="modal-contact">
              <h3>Contact</h3>
              <div className="contact-buttons">
                <a href={`tel:${selectedEngineer.phone.replace(/\s/g, '')}`} className="contact-btn phone">
                  <Phone size={18} /> {selectedEngineer.phone}
                </a>
                <a href={`mailto:${selectedEngineer.email}`} className="contact-btn email">
                  <Mail size={18} /> Email
                </a>
              </div>
            </div>

            <div className="modal-reviews">
              <div className="reviews-header">
                <h3>Reviews</h3>
                <Button variant="outline" size="sm" onClick={() => setShowReviewForm(!showReviewForm)}>
                  {showReviewForm ? 'Cancel' : 'Leave Review'}
                </Button>
              </div>

              {showReviewForm && (
                <form onSubmit={handleSubmitReview} className="review-form-new">
                  <Input
                    placeholder="Your name" value={reviewForm.customerName}
                    onChange={(e) => setReviewForm({ ...reviewForm, customerName: e.target.value })}
                    required
                  />
                  <Input
                    placeholder="Your phone number (for verification)" 
                    value={reviewForm.customerPhone}
                    onChange={(e) => setReviewForm({ ...reviewForm, customerPhone: e.target.value })}
                    type="tel"
                    required
                  />
                  <p className="review-phone-note">Your phone number is only used to verify the review and won't be published.</p>
                  <div className="star-input">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star} type="button"
                        className={`star-btn ${reviewForm.rating >= star ? 'active' : ''}`}
                        onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                      >
                        <Star size={24} fill={reviewForm.rating >= star ? 'currentColor' : 'none'} />
                      </button>
                    ))}
                  </div>
                  <textarea
                    placeholder="Your experience..." value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    required rows={3}
                  />
                  <Button type="submit" disabled={submittingReview}>
                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                  </Button>
                </form>
              )}

              <div className="reviews-list">
                {engineerReviews.length > 0 ? (
                  engineerReviews.map((review) => (
                    <div key={review.id} className="review-item-new">
                      <div className="review-top">
                        <span className="reviewer-name">{review.customerName}</span>
                        <div className="review-stars">
                          {[...Array(review.rating)].map((_, i) => <Star key={i} fill="currentColor" size={14} />)}
                        </div>
                      </div>
                      <p className="review-text">{review.comment}</p>
                      <span className="review-date">{new Date(review.date).toLocaleDateString('en-GB')}</span>
                    </div>
                  ))
                ) : (
                  <p className="no-reviews">No reviews yet. Be the first!</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Coverage Map Modal */}
      {showMap && (
        <div className="find-map-modal" onClick={() => setShowMap(false)} data-testid="find-map-modal">
          <div className="find-map-modal-inner" onClick={(e) => e.stopPropagation()}>
            <EngineerMap onClose={() => setShowMap(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default FindEngineer;
